import React, { useEffect, useState } from "react";
import { sunboundPayProxy } from '@proxies';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { useSessionContext, useSpinnerContext } from '@contexts';
import { Constants, helpers, Enums } from "@shared";
import { useFormSubmission, type FormError } from "@hooks";
import { PaymentMethodForm, IconButton, Button } from "@common";
import { ChevronLeftIcon } from "@icons";
import type { PayabliSuccessData, PayabliMessageData, PayabliConfig, PayabliComponentType, SessionData } from '@app-types';
import { PostHogEvents, capturePosthogEvent } from '@analytics';

interface LocationState {
    selectedPaymentType: number;
    account: SessionData;
    setupAccounts: SessionData[];
    hasMultipleAccounts: boolean;
}

const DefaultPaymentMethod = () => {

    const navigate = useNavigate(), location = useLocation() as Location<LocationState>;
    const { setSession } = useSessionContext();
    const { setSpinnerShow } = useSpinnerContext();
    const [payabli, setPayabli] = useState<PayabliComponentType | null>(null);
    const [pmReady, setPMReady] = useState(false);

    const { setFormState, clearFormState, hasFormError, formError } = useFormSubmission<FormError>();
    const {
        state: {
            selectedPaymentType,
            account,
            setupAccounts,
            hasMultipleAccounts
        }
    } = location;
    const {
        paypoint,
        idPayabli,
        idAccount,
        residentName
    } = account;

    // Determine payment method text based on selectedPaymentType
    const getPaymentMethodText = () => {
        if (selectedPaymentType === Enums.PaymentType.ACH) {
            return `Bank Account`;
        } else if (selectedPaymentType === Enums.PaymentType.Card) {
            return `Credit Card`;
        }
        return `Payment Method`;
    };

    const paymentMethodText = getPaymentMethodText();
    const isACHValidationFailed = selectedPaymentType === Enums.PaymentType.ACH
        && formError?.responseText === `Declined`
        && Number(formError?.resultCode) === 3
        && formError?.resultText?.toLowerCase().includes(`ach validation failed`);


    const payabliConfig: PayabliConfig = {
        type: `methodEmbedded`,
        rootContainer: `payabli`,
        defaultOpen: selectedPaymentType === Enums.PaymentType.ACH ? `ach` : `card`,
        token: window.settings.SUNBOUNDPAY_PAYABLI_PUBLIC_TOKEN,
        temporaryToken: false,
        entryPoint: paypoint,
        customCssUrl: PaymentMethodForm.customCssUrl,
        showPopoverError: false,
        card: {
            enabled: true,
            amex: true,
            discover: true,
            visa: true,
            mastercard: true,
            inputs: {
                cardHolderName: {
                    label: `Name on Card*`,
                    placeholder: `Name on card`,
                    floating: false,
                    value: ``,
                    size: 12,
                    row: 0,
                    order: 0
                },
                cardNumber: {
                    label: `Card Number`,
                    placeholder: `1234 1234 1234 1234`,
                    floating: false,
                    size: 6,
                    row: 1,
                    order: 0
                },
                cardExpirationDate: {
                    label: `Exp. Date*`,
                    placeholder: `MM/YY`,
                    floating: false,
                    size: 6,
                    row: 2,
                    order: 0
                },
                cardCvv: {
                    label: `CVV/CVC*`,
                    placeholder: `CVV/CVC`,
                    floating: false,
                    size: 6,
                    row: 2,
                    order: 1
                },
                cardZipcode: {
                    label: `ZIP/Postal Code*`,
                    placeholder: `Zip/Postal Code`,
                    floating: false,
                    size: 6,
                    row: 2,
                    order: 2
                }
            }
        },
        ach: {
            enabled: true,
            checking: true,
            savings: true,
            achValidation: true,
            inputs: {
                achAccountHolderName: {
                    label: `Account Holder Name*`,
                    placeholder: `Enter Account Holder Name`,
                    floating: false
                },
                achAccountType: {
                    label: `Account Type*`,
                    placeholder: `Select`,
                    floating: false
                },
                achAccount: {
                    label: `Account Number*`,
                    placeholder: `Enter Account Number`,
                    floating: false,
                    confirm: true
                },
                achRouting: {
                    label: `Routing Number*`,
                    placeholder: `Enter Routing Number`,
                    floating: false,
                    confirm: true
                }
            }
        },
        customerData: {
            customerId: idPayabli
        },
        functionCallBackSuccess: function (response) {
            window.postMessage({ message: `callSucceeded`, response });
        },
        functionCallBackReady: function (status) {
            window.postMessage({ message: `payabliReady`, status });
        },
        functionCallBackError: function (errors: unknown) {
            console.log(`functionCallBackError`, errors);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const callSucceeded = async (data: PayabliSuccessData) => {

        const { response: { isSuccess, responseText, responseData: { methodReferenceId: token, resultCode, resultText } } } = data;

        if (isSuccess === true || responseText === `Success`) {
            try {
                const dpm = await sunboundPayProxy.addPaymentMethod(idAccount, token, true);
                capturePosthogEvent(PostHogEvents.PAYMENT_METHOD_ADDED, {
                    paymentType: selectedPaymentType,
                    isDefault: true
                });

                // Ensure other pages can retrieve the new default payment method
                // from the session data; the session data should not be stale
                const session = await sunboundPayProxy.refreshSession();
                setSession(session);

                if (hasMultipleAccounts === true) {
                    if (setupAccounts.length > 0) {
                        const [nextAccount, ...rest] = setupAccounts;
                        helpers.navigateTo(`/existing-payment-methods`, location, navigate, undefined, { account: nextAccount, setupAccounts: [...rest] });
                    } else {
                        helpers.navigateNext(location, navigate, { missingAccounts: false });
                    }
                } else {
                    const { paymentPreferences } = account;
                    const newAccount = { ...account, paymentPreferences: { ...paymentPreferences, defaultPaymentMethod: dpm } };
                    helpers.navigateNext(location, navigate, undefined, { account: newAccount });
                }

            } catch (error) {
                console.log(error);
            } finally {
                setSpinnerShow(false);
            }
        } else {
            setSpinnerShow(false);
            console.log(`Error (${resultCode}): ${resultText}`);
            setFormState({ responseText, resultCode, resultText });
        }
    };

    const initRegistration = () => {
        if (!payabli) return;
        setSpinnerShow(true);
        try {
            payabli.payabliExec(`method`);
        } catch (error) {
            console.log(error);
            setSpinnerShow(false);
        }

    };

    const onMessageReceivedFromIframe = React.useCallback(
        async (event: MessageEvent<PayabliMessageData>) => {
            const { data } = event;
            if (data) {
                if (data.message === `payabliReady`) {
                    clearFormState();
                    const [, formReady] = data.status;
                    setPMReady(formReady);
                }
                if (data.message === `callSucceeded`) {
                    await callSucceeded(data);
                }
            }
        },
        [payabli]
    );

    useEffect(() => {
        setSpinnerShow(true);
        const component = new PayabliComponent(payabliConfig);
        setPayabli(component);
    }, []);

    const payabliLoaded = () => setSpinnerShow(false);

    useEffect(() => {
        window.addEventListener(`message`, onMessageReceivedFromIframe);
        if (payabli && payabli.payabliIframe.addEventListener)
            payabli.payabliIframe.addEventListener(`load`, payabliLoaded);

        return () => {
            window.removeEventListener(`message`, onMessageReceivedFromIframe);
            if (payabli && payabli.payabliIframe.removeEventListener)
                payabli.payabliIframe.removeEventListener(`load`, payabliLoaded);
        };
    }, [onMessageReceivedFromIframe]);

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <div className="flex flex-col w-full">
            {/* Header with back button and title */}
            <div className="border-b border-neutral-100 pb-6 mb-8">
                <div className="flex items-center gap-2">
                    <IconButton
                        variant="tertiary"
                        size="sm"
                        className="flex-shrink-0 md:btn-icon-lg"
                        onClick={handleCancel}
                        aria-label="Go back"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">
                        Add {paymentMethodText}
                    </h1>
                </div>
            </div>

            {/* Resident context */}
            {residentName && (
                <p className="text-base leading-6 text-neutral-950 tracking-[0.16px] mb-4">
                    For {residentName}
                </p>
            )}

            <div className="flex flex-col gap-[32px]">
                {/* Payment form iframe */}
                <div id="payabli" className="w-full"></div>

                {/* Buttons */}
                <div className="flex flex-col md:flex-row gap-4 w-full">
                    <Button
                        variant="secondary"
                        size="lg"
                        className="order-2 md:order-1 w-full"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        className="order-1 md:order-2 w-full"
                        disabled={!pmReady}
                        onClick={initRegistration}
                    >
                        Add {paymentMethodText}
                    </Button>
                </div>
            </div>

            {/* Error message */}
            {hasFormError && (
                isACHValidationFailed ? (
                    <p className="text-red-600 text-sm mt-4">
                        {Constants.Errors.ACH_VALIDATION_FAILED}
                    </p>
                ) : (
                    <p className="text-red-600 text-sm mt-4">
                        There was an issue registering your payment method. Please verify your payment information and try again. For more details, see our <a className="text-blue-600 hover:text-blue-700 underline focus-visible:outline-focus-ring rounded" href={Constants.Help.FAQErrors} target="_blank" rel="noreferrer">FAQ</a>.
                    </p>
                )
            )}
        </div>
    );
};

export default DefaultPaymentMethod;
